/**
 * `Actions` is the ONLY way to mutate editor state (plan D25). Every UI module dispatches
 * through it; nothing writes state directly.
 *
 * Each action is wrapped by the store so that (a) it notifies subscribers exactly once, and
 * (b) `state.history` is re-synced from the undo stack afterwards.
 */

import type { ActionContext } from './context';
import { createClipActions, type ClipActions } from './clip';
import { createCompanionActions, type CompanionActions } from './companions';
import { createErrorActions, createHistoryActions, type ErrorActions, type HistoryActions } from './errors';
import { createEventActions, type EventActions } from './events';
import { createIkActions, type IkActions } from './ik';
import { createKeyActions, type KeyActions } from './keys';
import { createLibraryActions, type LibraryActions } from './library';
import { createMappingActions, type MappingActions } from './mapping';
import { createModeActions, type ModeActions } from './mode';
import { createPoseActions, type PoseActions } from './pose';
import { createRigActions, type RigActions } from './rig';

export type Actions = ModeActions &
  PoseActions &
  IkActions &
  ClipActions &
  KeyActions &
  EventActions &
  MappingActions &
  RigActions &
  LibraryActions &
  CompanionActions &
  ErrorActions &
  HistoryActions;

export function createActions(ctx: ActionContext): Actions {
  return {
    ...createModeActions(ctx),
    ...createPoseActions(ctx),
    ...createIkActions(ctx),
    ...createClipActions(ctx),
    ...createKeyActions(ctx),
    ...createEventActions(ctx),
    ...createMappingActions(ctx),
    ...createRigActions(ctx),
    ...createLibraryActions(ctx),
    ...createCompanionActions(ctx),
    ...createErrorActions(ctx),
    ...createHistoryActions(ctx),
  };
}

export type { ActionContext } from './context';
export { evaluatedPose } from './context';
export type { ClipActions } from './clip';
export type { CompanionActions } from './companions';
export type { ErrorActions, HistoryActions } from './errors';
export { DEFAULT_EVENT_NAME, resolveEvent } from './events';
export type { EventActions } from './events';
export type { IkActions } from './ik';
export type { KeyActions } from './keys';
export type { LibraryActions } from './library';
export type { MappingActions } from './mapping';
export type { ModeActions } from './mode';
export type { PoseActions } from './pose';
export type { RigActions } from './rig';
export { canEnterMode, isRigReadyForAnimate, modeRejectionReason } from './mode';
export { mirroredBone } from './rig';
export { clipLengthSeconds } from './clip';
